import acc from "accounting"
import { Flex, Tooltip, Typography } from "antd"

import { countryFlagsByCountryCode } from "../../../../Components/Filters/filterOptionsData"
import InfoTooltip from "../../../../Components/InfoTooltip/InfoTooltip"
import Private from "../../../../Components/Private"
import { titleSortFilters } from "../../../../Components/titleSortFilters"
import Icon from "../../../../Icon"
import Images from "../../../../Images"
import { cardBrandToTextMap } from "../../utils/cardOptions"

const { Text } = Typography

export const formatCount = (am) => acc.formatNumber(am, 0, ",", ".")
export const formatAmount = (am) => `$${acc.formatNumber(am, 2, ",", ".")}`

export const getDefaultColumns = ({ t } = {}) => [
  {
    key: "chargebacks",
    title: t("Chargebacks"),
    dataIndex: "chargebacks",
    align: "right",
    render: (_, record) => {
      if (record?.isDivider) return null
      return (
        <Flex gap={2} vertical={true}>
          <Text strong>{formatCount(record?.["chargebacks_count"])}</Text>
          <Text type="secondary" strong>
            <Private>{formatAmount(record?.["chargebacks_amount"])}</Private>
          </Text>
        </Flex>
      )
    }
  },
  {
    key: "alerts",
    title: t("Alerts"),
    dataIndex: "alerts",
    align: "right",
    render: (_, record) => {
      if (record?.isDivider) return null
      return (
        <Flex gap={2} vertical={true}>
          <Text strong>{formatCount(record?.["alerts_count"])}</Text>
          <Text type="secondary" strong>
            <Private>{formatAmount(record?.["alerts_amount"])}</Private>
          </Text>
        </Flex>
      )
    }
  },
  {
    key: "chargeback_rate",
    title: t("Chargeback rate"),
    dataIndex: "chargeback_rate",
    align: "right",
    render: (_, record) => {
      if (record?.isDivider) return null
      const isMoreThan100 = record?.["chargeback_rate"] > 100
      return (
        <Flex gap={2} vertical={true} align="flex-end">
          <Flex gap={4}>
            <Text strong>{record?.["chargeback_rate"]}%</Text>
            {isMoreThan100 && <ChargebackRateOver100Tooltip />}
          </Flex>
          <Text type="secondary" strong>
            {`${record?.["chargeback_rate_without_us"]}% without us`}
          </Text>
        </Flex>
      )
    }
  },
  {
    key: "prevention_rate",
    title: t("Prevention rate"),
    dataIndex: "prevention_rate",
    align: "right",
    render: (_, record) => {
      if (record?.isDivider) return null
      return <Text strong>{_}%</Text>
    }
  }
]

export const getColumnsByCountry = ({ t, withSort } = {}) => {
  return [
    {
      key: "country",
      sorter: withSort,
      title: (state) =>
        titleSortFilters({
          title: t("Country"),
          sorter: withSort,
          stateData: state,
          key: "country"
        }),
      dataIndex: "country",
      render: (_, record) => {
        const code = record?.country_code?.toUpperCase()
        let image = countryFlagsByCountryCode?.[code] || null
        return (
          <Flex gap={8}>
            {image && <img src={image} alt={_} width={20} />}
            <Text strong>{cardBrandToTextMap?.[_] || _}</Text>
          </Flex>
        )
      }
    },
    {
      key: "transactions",
      sorter: withSort,
      title: (state) =>
        titleSortFilters({
          title: t("Transactions"),
          sorter: withSort,
          stateData: state,
          key: "transactions_count",
          justify: "end"
        }),
      dataIndex: "transactions_count",
      align: "right",
      render: (_, record) => (
        <Flex gap={2} vertical={true}>
          <Text strong>{formatCount(record?.["transactions_count"])}</Text>
          <Text type="secondary" strong>
            <Private>{formatAmount(record?.["transactions_amount"])}</Private>
          </Text>
        </Flex>
      )
    },
    {
      key: "chargebacks",
      sorter: withSort,
      title: (state) =>
        titleSortFilters({
          title: t("Chargebacks"),
          sorter: withSort,
          stateData: state,
          key: "chargebacks_count",
          justify: "end"
        }),
      dataIndex: "chargebacks_count",
      align: "right",
      render: (_, record) => (
        <Flex gap={2} vertical={true}>
          <Text strong>{formatCount(record?.["chargebacks_count"])}</Text>
          <Text type="secondary" strong>
            <Private>{formatAmount(record?.["chargebacks_amount"])}</Private>
          </Text>
        </Flex>
      )
    },
    {
      key: "alerts",
      sorter: withSort,
      title: (state) =>
        titleSortFilters({
          title: t("Alerts"),
          sorter: withSort,
          stateData: state,
          key: "alerts_count",
          justify: "end"
        }),
      dataIndex: "alerts_count",
      align: "right",
      render: (_, record) => (
        <Flex gap={2} vertical={true}>
          <Text strong>{formatCount(record?.["alerts_count"])}</Text>
          <Text type="secondary" strong>
            <Private>{formatAmount(record?.["alerts_amount"])}</Private>
          </Text>
        </Flex>
      )
    },
    {
      key: "chargeback_rate",
      title: t("Chargeback rate"),
      dataIndex: "chargeback_rate",
      align: "right",
      render: (_, record) => {
        const isMoreThan100 = record?.["chargeback_rate"] > 100
        return (
          <Flex gap={2} vertical={true} align="flex-end">
            <Flex gap={4}>
              <Text strong>{record?.["chargeback_rate"]}%</Text>
              {isMoreThan100 && <ChargebackRateOver100Tooltip />}
            </Flex>
            <Text type="secondary" strong>
              {`${record?.["chargeback_rate_without_us"]}% without us`}
            </Text>
          </Flex>
        )
      }
    },
    {
      key: "prevention_rate",
      sorter: withSort,
      title: (state) =>
        titleSortFilters({
          title: t("Prevention rate"),
          sorter: withSort,
          stateData: state,
          key: "prevention_rate",
          justify: "end"
        }),
      dataIndex: "prevention_rate",
      align: "right",
      render: (_) => <Text strong>{_}%</Text>
    }
  ]
}

export const getColumnsByReasonCode = ({ t, withSort, theme } = {}) => {
  return [
    {
      key: "reason_code",
      sorter: withSort,
      title: (state) =>
        titleSortFilters({
          title: t("Reason code"),
          sorter: withSort,
          stateData: state,
          key: "reason_code"
        }),
      dataIndex: "reason_code",
      render: (_, record) => {
        let image = Images?.[record?.card_brand?.toLowerCase()] || null
        return (
          <Flex gap={8}>
            {image && <img src={image} alt={_} width={20} />}
            <Text>{_}</Text>
            {record.tooltip && (
              <Tooltip
                title={record.tooltip}
                placement="top"
                styles={{
                  body: {
                    width: "450px"
                  }
                }}
              >
                <div className="text-with-icon">
                  <Icon type="circle_info" style={{ fontSize: 16, color: "#A1A1AA" }} />
                </div>
              </Tooltip>
            )}
          </Flex>
        )
      }
    },
    {
      key: "type",
      sorter: withSort,
      title: (state) =>
        titleSortFilters({
          title: t("Type"),
          sorter: withSort,
          stateData: state,
          key: "type",
          justify: "end"
        }),
      dataIndex: "type",
      align: "right"
    },
    {
      key: "chargebacks",
      sorter: withSort,
      title: (state) =>
        titleSortFilters({
          title: t("Chargebacks"),
          sorter: withSort,
          stateData: state,
          key: "chargebacks_count",
          justify: "end"
        }),
      dataIndex: "chargebacks_count",
      align: "right",
      render: (_, record) => (
        <Flex gap={2} vertical={true}>
          <Text>{formatCount(record?.["chargebacks_count"])}</Text>
          <Text type="secondary">{formatAmount(record?.["chargebacks_amount"])}</Text>
        </Flex>
      )
    },
    {
      key: "chargebacks_without_us",
      sorter: withSort,
      title: (state) =>
        titleSortFilters({
          title: t("Chargebacks without us"),
          sorter: withSort,
          stateData: state,
          key: "chargebacks_count_without_us",
          justify: "end"
        }),
      dataIndex: "chargebacks_count_without_us",
      align: "right",
      render: (_, record) => (
        <Flex gap={2} vertical={true}>
          <Text>{formatCount(record?.["chargebacks_count_without_us"])}</Text>
          <Text type="secondary">{formatAmount(record?.["chargebacks_amount_without_us"])}</Text>
        </Flex>
      )
    },
    {
      key: "alerts",
      sorter: withSort,
      title: (state) =>
        titleSortFilters({
          title: t("Alerts"),
          sorter: withSort,
          stateData: state,
          key: "alerts_count",
          justify: "end"
        }),
      dataIndex: "alerts_count",
      align: "right",
      render: (_, record) => (
        <Flex gap={2} vertical={true}>
          <Text>{formatCount(record?.["alerts_count"])}</Text>
          <Text type="secondary">{formatAmount(record?.["alerts_amount"])}</Text>
        </Flex>
      )
    },
    {
      key: "prevention_rate",
      sorter: withSort,
      title: (state) =>
        titleSortFilters({
          title: t("Prevention rate"),
          sorter: withSort,
          stateData: state,
          key: "prevention_rate",
          justify: "end"
        }),
      dataIndex: "prevention_rate",
      align: "right",
      render: (_) => <Text>{_}%</Text>
    }
  ]
}

export const getRowPercentage = (record, data = []) => {
  if (!record || !data || data.length === 0) return 0

  const amounts = data.map((item) => {
    const amount = item?.transactions_count
    return typeof amount === "number" && !isNaN(amount) ? amount : 0
  })

  const maxAmount = Math.max(...amounts)
  const currentAmount = record?.transactions_count

  if (maxAmount <= 0 || typeof currentAmount !== "number" || isNaN(currentAmount)) {
    return 0
  }

  return Math.ceil((currentAmount / maxAmount) * 100)
}

export const ChargebackRateOver100Tooltip = () => {
  const reasons = [
    "Low transaction volume in the current period",
    "Chargebacks from previous months appearing now",
    "Multiple disputes on the same transaction(s)"
  ]
  return (
    <InfoTooltip
      title={
        <Flex vertical gap={12}>
          Chargeback rate can exceed 100% because:
          <Flex gap={4} vertical>
            {reasons.map((line, index) => (
              <Flex key={index} gap={6}>
                <div>•</div>
                <div>{line}</div>
              </Flex>
            ))}
          </Flex>
        </Flex>
      }
      overlayInnerStyle={{ minWidth: "320px" }}
    />
  )
}
